"use client";
/**
 * गोत्र EP01 — ACT 1 (0:00 – 1:18).  HOOK · SETUP · TWO SYSTEMS · आर्ष · लौकिक
 *
 * ⛔ THREE LAWS BOUGHT ON THIS ACT (Yash, 2026-08-11):
 *
 * 1. BEATS MAY NOT OVERLAP IN TIME. The 0:58 pile-up was not a layout bug — Beat3 ran to 61.0s
 *    while Beat4 started at 53.9s, so seven seconds rendered BOTH beats on top of each other.
 *    Every beat below now ends before the next one starts. Check the table, not the eye.
 *
 * 2. TEXT LIVES IN <Panel> (flow layout), never at a hand-typed x/y. Devanagari width is unknowable
 *    at author time, so absolute text wraps past its box and lands on the block below.
 *
 * 3. ⭐ THE SYSTEM BOXES ARE ONE OBJECT, AND THE ACTIVE ONE BECOMES THE PAGE HEADING.
 *    Yash: "when you introduce two vyavastha, at that time you only said aarsh — so only aarsh
 *    should be visible and the other blurred ... when you shift to explanation of aarsh, the
 *    aarsh you unblurred should have become the heading of the page."
 *    So: two boxes arrive BLURRED → आर्ष sharpens on its own word → it then TRAVELS and SCALES
 *    into the heading slot and stays there for the whole आर्ष explanation → at लौकिक it returns
 *    to its box, dims, and लौकिक sharpens and rises into the heading in the same move.
 *    This replaces four caption blocks: the heading says which system we are inside.
 */
import React from "react";
import { staticFile } from "remotion";
import {
  Run, Cap, Chip, Dot, Stroke, Prop, Persist, PackRow, GridToStrip, LightSweep, Beat, Panel,
  useKeys, useSpringAt, useStep, useF, deva,
  INK, GOLD, KUMKUM, MUTED,
} from "./craft";

const C = (n: string) => staticFile(`sanatan/cut/${n}.png`);

/* ══════════════════════════════════════════════════════════════
   THE SYSTEM PAIR — persistent, stage level, outside every <Beat>.

   pose 0 (36.0s)  both boxes arrive, BOTH BLURRED
   pose 1 (39.8s)  आर्ष sharpens (its word)
   pose 2 (43.5s)  आर्ष travels up-left and scales into the HEADING
   pose 3 (61.4s)  आर्ष returns to its box and dims; लौकिक sharpens
   pose 4 (62.5s)  लौकिक rises into the HEADING
   ══════════════════════════════════════════════════════════════ */
const BOX_Y = 392, BOX_W = 300, BOX_H = 104;
const HEAD_X = 54, HEAD_Y = 40;

const SysBox: React.FC<{
  label: string; gloss: string; icon: string; accent: string;
  boxX: number; sharpAt: number; headAt: number; headOut: number; dimAt: number; out: number;
}> = ({ label, gloss, icon, accent, boxX, sharpAt, headAt, headOut, dimAt, out }) => {
  const frame = useStep(); const F = useF();
  const sIn = useSpringAt(36.0, 9, 110, 1.0);
  const sOut = useSpringAt(out, 200, 80, 0.5);

  // blur: starts blurred, sharpens on its word, dims again when the other takes over
  const blur = useKeys([[36.0, 7], [sharpAt, 7], [sharpAt + 0.5, 0], [dimAt, 0], [dimAt + 0.6, 6]]);
  const dim = useKeys([[36.0, 0.55], [sharpAt, 0.55], [sharpAt + 0.5, 1], [dimAt, 1], [dimAt + 0.6, 0.5]]);
  // travel into the heading slot and back
  const x = useKeys([[headAt - 0.4, boxX], [headAt + 0.7, HEAD_X], [headOut, HEAD_X], [headOut + 0.9, boxX]]);
  const y = useKeys([[headAt - 0.4, BOX_Y], [headAt + 0.7, HEAD_Y], [headOut, HEAD_Y], [headOut + 0.9, BOX_Y]]);
  const k = useKeys([[headAt - 0.4, 1], [headAt + 0.7, 1.34], [headOut, 1.34], [headOut + 0.9, 1]]);

  if (frame < F(35.4) || frame > F(out + 1.2)) return null;
  const o = Math.min(1, sIn * 1.5) * (1 - Math.min(1, sOut * 1.4));
  return (
    <div style={{
      position: "absolute", left: x, top: y, width: BOX_W, zIndex: 24,
      transform: `scale(${k}) translateY(${(1 - sIn) * 28}px)`, transformOrigin: "0 0",
      opacity: o * dim, filter: `blur(${blur}px)`,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 14, height: BOX_H, padding: "0 18px",
        background: "rgba(255,251,240,0.94)", borderRadius: 10,
        border: `2.5px solid ${accent}`, boxShadow: "0 8px 20px rgba(72,46,16,0.20)",
      }}>
        <img src={C(icon)} style={{ height: BOX_H - 26, filter: "drop-shadow(0 3px 5px rgba(72,46,16,0.3))" }} />
        <div>
          <div style={{ fontFamily: deva, fontWeight: 900, fontSize: 34, color: accent, lineHeight: 1.1 }}>{label}</div>
          <div style={{ fontFamily: deva, fontWeight: 700, fontSize: 17, color: MUTED, lineHeight: 1.2 }}>{gloss}</div>
        </div>
      </div>
    </div>
  );
};

export const SystemPair: React.FC = () => (
  <>
    <SysBox label="आर्ष" gloss="रिषि से" icon="vd_sage_seated" accent={KUMKUM}
      boxX={300} sharpAt={39.8} headAt={43.5} headOut={61.0} dimAt={61.0} out={77.4} />
    <SysBox label="लौकिक" gloss="लोक से" icon="pp_genealogist" accent={GOLD}
      boxX={680} sharpAt={62.5} headAt={63.2} headOut={77.0} dimAt={1e6} out={77.4} />
  </>
);

/* ══ BEAT 0 · HOOK — 0:00-0:15.2 ═══════════════════════════════ */
export const Beat0: React.FC = () => {
  const OUT = 15.0;
  return (
    <Beat a={0} b={15.2}>
      {/* ⭐ each occasion arrives BIG in a 3-across grid filling the canvas, on its own spoken
          word; at 7.0s all six travel down into the bottom strip together and stay as context
          while the pandit asks the question. Same six objects throughout. */}
      <GridToStrip shiftAt={7.0} out={OUT} cols={3} gridTop={64} gridH={258} bigH={210}
        stripBaseline={628} stripH={104}
        items={[
          { k: "bi_cradle", at: 0.84, label: "जन्म" },
          { k: "rt_razor", at: 1.58, label: "मुंडन" },
          { k: "rt_mandap", at: 2.40, label: "शादी" },
          { k: "rt_threshold", at: 3.28, label: "गृह प्रवेश" },
          { k: "rt_havan", at: 4.26, label: "हवन" },
          { k: "rt_pot", at: 5.94, label: "अंतिम संस्कार" },
        ]} />
      <Prop src={C("pp_pandit")} h={236} left={980} top={168} at={8.55} out={OUT} from={-1} z={20} />
      <Panel x={54} y={96} w={520} gap={24} at={8.6} out={15.0}>
        <Run from={23} to={29} size={31} color={MUTED} out={OUT} />
        <Run from={30} to={33} size={62} out={OUT} accentAt={31} />
      </Panel>
    </Beat>
  );
};

/* ══ BEAT 1 · SETUP — 0:15.2-0:29.2 ════════════════════════════ */
export const Beat1: React.FC = () => {
  const OUT = 29.0;
  return (
    <Beat a={15.2} b={29.2}>
      <LightSweep at={15.9} />
      <Prop src={C("pp_phoneman")} h={290} left={1020} top={250} at={16.2} out={OUT} from={-1} />
      <Panel x={54} y={78} w={470} gap={20} at={17.6} out={29.0}>
        <Run from={52} to={55} size={52} out={OUT} accentAt={52} />
      </Panel>
      <Stroke d="M 70 430 L 590 430" at={19.9} out={OUT} color={GOLD} w={2} dur={0.7} />
      <Dot x={130} y={430} r={9} at={20.1} out={OUT} color={KUMKUM} />
      <Dot x={330} y={430} r={9} at={21.3} out={OUT} color={GOLD} />
      <Dot x={530} y={430} r={9} at={22.1} out={OUT} color={GOLD} />
      <Chip text="गोत्र" x={102} y={448} at={20.2} out={OUT} color={KUMKUM} size={24} />
      <Chip text="प्रवर" x={300} y={448} at={21.4} out={OUT} color={GOLD} size={24} />
      <Chip text="सपिंड" x={498} y={448} at={22.2} out={OUT} color={GOLD} size={24} />
    </Beat>
  );
};

/* ══ BEAT 2 · TWO SYSTEMS — 0:29.2-0:42.9 ══════════════════════
   The boxes do the talking (SystemPair, stage level). Text is one line. */
export const Beat2: React.FC = () => {
  const OUT = 42.6;
  return (
    <Beat a={29.2} b={42.9}>
      <LightSweep at={29.9} />
      <Panel x={54} y={78} w={392} gap={16} at={33.1} out={40.5}>
        <Run from={100} to={111} size={40} out={OUT} accentAt={100} />
      </Panel>
      <Stroke d="M 636 300 L 636 336" at={35.0} out={OUT} color={INK} w={2.6} dur={0.3} />
      <Stroke d="M 636 336 C 636 366, 450 358, 450 388" at={35.6} out={OUT} color={KUMKUM} w={2.6} dur={0.5} />
      <Stroke d="M 636 336 C 636 366, 830 358, 830 388" at={35.9} out={OUT} color={GOLD} w={2.6} dur={0.5} />
      <Panel x={556} y={228} w={340} gap={8} align="center" at={35.3} out={42.6}>
        <Cap text="दो अलग व्यवस्थाएँ" at={35.4} out={OUT} size={32} color={INK} />
      </Panel>
    </Beat>
  );
};

/* ══ BEAT 3 · आर्ष — 0:42.9-1:00.9 ═════════════════════════════
   The आर्ष box is now the page HEADING. The patriline walks back and stops on the rishi.
   The भारद्वाज / कश्यप examples belong to THIS beat (segment 6 ends at 60.78s) — putting them
   in the next beat is what caused the 0:58 pile-up. */
export const Beat3: React.FC = () => {
  const OUT = 60.6;
  const Y = 268;
  return (
    <Beat a={42.9} b={60.9}>
      <LightSweep at={43.3} />

      {/* the chain — one dot per spoken पिता, walking back in time */}
      <Dot x={1150} y={Y} r={10} at={43.9} out={54.0} color={INK} />
      <Chip text="आप" x={1124} y={Y + 22} at={44.0} out={54.0} size={18} color={MUTED} />
      <Stroke d={`M 1140 ${Y} L 1040 ${Y}`} at={44.0} out={54.0} color={INK} w={2.2} dur={0.3} />
      <Dot x={1026} y={Y} r={10} at={44.2} out={54.0} color={INK} />
      <Chip text="पिता" x={998} y={Y + 22} at={44.3} out={54.0} size={18} color={MUTED} />
      <Stroke d={`M 1016 ${Y} L 916 ${Y}`} at={45.2} out={54.0} color={INK} w={2.2} dur={0.3} />
      <Dot x={902} y={Y} r={10} at={45.4} out={54.0} color={INK} />
      <Chip text="पिता" x={874} y={Y + 22} at={45.5} out={54.0} size={18} color={MUTED} />
      <Stroke d={`M 892 ${Y} L 792 ${Y}`} at={46.1} out={54.0} color={INK} w={2.2} dur={0.3} />
      <Dot x={778} y={Y} r={10} at={46.4} out={54.0} color={INK} />
      <Chip text="पिता" x={750} y={Y + 22} at={46.5} out={54.0} size={18} color={MUTED} />
      <Stroke d={`M 768 ${Y} L 640 ${Y}`} at={47.9} out={54.0} color={MUTED} w={2} dur={0.9} dash />
      <Dot x={624} y={Y} r={14} at={49.9} out={54.0} color={KUMKUM} ring />

      <Panel x={54} y={470} w={520} gap={14} at={51.4} out={54.0}>
        <Run from={155} to={162} size={36} out={54.0} accentAt={161} underAt={161} />
      </Panel>

      {/* the two examples — the rishi is standing right there while they are named */}
      <Panel x={620} y={150} w={400} gap={14} at={54.1} out={60.6}>
        <Cap text="भारद्वाज गोत्र" at={54.14} out={OUT} size={30} color={KUMKUM} weight={800} />
        <Cap text="भारद्वाज रिषि से गिना जाता है" at={56.2} out={OUT} size={23} />
        <Cap text="कश्यप गोत्र" at={58.9} out={OUT} size={30} color={KUMKUM} weight={800} />
      </Panel>
    </Beat>
  );
};

/** ONE rishi — arrives where the patriline stops, travels left and shrinks, leaves at 60.9. */
export const ActRishi: React.FC = () => (
  <Persist src={C("rishi")} at={49.9} out={60.6} z={22}
    poses={[[49.9, 380, 120, 290], [53.6, 380, 120, 290], [54.8, 180, 250, 230], [60.6, 180, 250, 230]]} />
);

/* ══ BEAT 4 · लौकिक — 1:00.9-1:18 ══════════════════════════════
   लौकिक is now the heading. Real objects carry the four sources of the name. */
export const Beat4: React.FC = () => {
  const OUT = 77.2;
  return (
    <Beat a={60.9} b={78.0}>
      <LightSweep at={61.4} />
      <PackRow out={OUT} baseline={620} x={620} w={620} gap={18} z={20}
        items={[
          { k: "pp_genealogist", h: 250, at: 64.4 },
          { k: "ic_village", h: 170, at: 73.4 },
        ]} />
      <Panel x={54} y={150} w={470} gap={18} at={63.9} out={71.5}>
        <Run from={189} to={198} size={30} color={MUTED} out={OUT} />
      </Panel>

      <Stroke d="M 80 430 L 560 430" at={71.9} out={OUT} color={GOLD} w={2} dur={0.7} />
      <Dot x={108} y={430} r={7} at={72.3} out={OUT} color={GOLD} />
      <Dot x={248} y={430} r={7} at={73.4} out={OUT} color={GOLD} />
      <Dot x={388} y={430} r={7} at={74.3} out={OUT} color={GOLD} />
      <Dot x={528} y={430} r={7} at={75.4} out={OUT} color={GOLD} />
      <Chip text="पुरखा" x={76} y={448} at={72.3} out={OUT} color={INK} size={21} />
      <Chip text="गाँव" x={222} y={448} at={73.4} out={OUT} color={INK} size={21} />
      <Chip text="काम" x={362} y={448} at={74.3} out={OUT} color={INK} size={21} />
      <Chip text="वंश" x={504} y={448} at={75.4} out={OUT} color={INK} size={21} />

      <Panel x={54} y={520} w={520} gap={12} at={76.1} out={77.2}>
        <Run from={226} to={232} size={32} out={OUT} accentAt={230} />
      </Panel>
    </Beat>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
